// In-memory normalized graph built from GET /v1/sync. Mirrors the SwiftData
// graph the iOS app reconstructs (PROJECT → ROOM → PHOTO/SESSION/IDEA/SCAN,
// SESSION → CYCLE). Joins are by parentId (docs/API.md §Sync).

import { ApiError, fetchMemberships, syncPull } from "./apiClient";
import type { Membership, ProjectRole, SyncPullPage } from "./apiClient";
import type {
  AssetRef,
  CyclePayload,
  EntityType,
  IdeaPayload,
  PhotoPayload,
  ProjectPayload,
  RoomPayload,
  ScanPayload,
  SessionPayload,
  SyncRecord,
} from "./types";

export interface StoredEntity<P = Record<string, unknown>> {
  type: EntityType;
  id: string;
  parentId: string | null;
  version: number;
  updatedAt: string;
  deleted: boolean;
  payload: P;
  assets: AssetRef[];
  /** Shared-project scope this record was pulled from; null = the caller's own partition. */
  scopeProjectId: string | null;
}

const key = (type: EntityType, id: string) => `${type}#${id}`;

/** Parent-chain ladder (PROJECT → ROOM → leaf; SESSION → CYCLE). */
const PARENT_TYPE: Partial<Record<EntityType, EntityType>> = {
  ROOM: "PROJECT",
  PHOTO: "ROOM",
  SESSION: "ROOM",
  IDEA: "ROOM",
  SCAN: "ROOM",
  CYCLE: "SESSION",
};

function parseAssets(payload: Record<string, unknown> | undefined): AssetRef[] {
  const raw = payload?.assets;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is AssetRef =>
      !!a && typeof a === "object" && typeof (a as AssetRef).s3Key === "string",
  );
}

export class EntityStore {
  private byKey = new Map<string, StoredEntity>();
  private assetByLocalPath = new Map<string, AssetRef>();
  /** Editor/viewer memberships by projectId (own `role:"owner"` rows are skipped). */
  private membershipsById = new Map<string, Membership>();
  /** Per-scope pull cursors: "own" and "project:<pid>". */
  cursors = new Map<string, string>();

  /**
   * Full bootstrap. Memberships first (404 ⇒ old backend ⇒ own-only), then the
   * own partition, then one scoped pull per shared project. A 403 on a scope
   * (revoked / project deleted) drops that membership and continues — one lost
   * share never fails the whole load.
   */
  async load(): Promise<void> {
    const memberships = (await membershipsWithRetry()) ?? [];
    this.membershipsById.clear();
    for (const m of memberships) {
      // Own role:"owner" rows are returned too; own projects arrive via the
      // unscoped pull, so scoping them would double-deliver. Skip.
      if (m.role === "owner") continue;
      this.membershipsById.set(m.projectId, m);
    }

    await this.loadScope(undefined);
    for (const m of [...this.membershipsById.values()]) {
      try {
        await this.loadScope(m.projectId);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          // not_a_member: revoked (or deleted) since the memberships fetch.
          this.purgeScope(m.projectId);
          continue;
        }
        throw e;
      }
    }
  }

  /** Page one scope's delta stream from its watermark. `projectId` undefined = own scope. */
  private async loadScope(projectId: string | undefined): Promise<void> {
    const scope = projectId ? `project:${projectId}` : undefined;
    const cursorKey = scope ?? "own";
    let since = this.cursors.get(cursorKey) ?? "0";
    // Guard against a runaway loop; generous headroom over today's record counts.
    for (let page = 0; page < 200; page++) {
      const res = await pullWithRetry(since, scope);
      for (const rec of res.records) this.apply(rec, projectId ?? null);
      since = res.nextCursor;
      this.cursors.set(cursorKey, since);
      if (!res.hasMore) break;
    }
  }

  /**
   * Apply a pulled/locally-written record. When `scopeProjectId` is omitted
   * (local mutations), the record keeps whatever scope it already had — a rename
   * of a shared room must not strand it outside its purge scope.
   */
  apply(rec: SyncRecord, scopeProjectId?: string | null): void {
    const k = key(rec.entityType, rec.entityId);
    const entity: StoredEntity = {
      type: rec.entityType,
      id: rec.entityId,
      parentId: rec.parentId ?? null,
      version: rec.version,
      updatedAt: rec.updatedAt,
      deleted: rec.deleted,
      payload: rec.payload ?? {},
      assets: parseAssets(rec.payload),
      scopeProjectId:
        scopeProjectId !== undefined ? scopeProjectId : (this.byKey.get(k)?.scopeProjectId ?? null),
    };
    this.byKey.set(k, entity);
    for (const a of entity.assets) this.assetByLocalPath.set(a.localPath, a);
  }

  /**
   * Drop everything pulled for a shared project (revocation, leave, project
   * deleted): its records, its membership row, and its scope cursor. Local-only;
   * never tombstones anything.
   */
  purgeScope(projectId: string): void {
    for (const [k, e] of [...this.byKey]) {
      if (e.scopeProjectId !== projectId) continue;
      this.byKey.delete(k);
      for (const a of e.assets) {
        if (this.assetByLocalPath.get(a.localPath) === a) this.assetByLocalPath.delete(a.localPath);
      }
    }
    this.membershipsById.delete(projectId);
    this.cursors.delete(`project:${projectId}`);
  }

  /** After a successful POST /v1/sync, record the new server version locally. */
  noteApplied(type: EntityType, id: string, version: number, updatedAt: string): void {
    const e = this.byKey.get(key(type, id));
    if (e) {
      e.version = version;
      e.updatedAt = updatedAt;
    }
  }

  get<P = Record<string, unknown>>(type: EntityType, id: string): StoredEntity<P> | undefined {
    const e = this.byKey.get(key(type, id)) as StoredEntity<P> | undefined;
    // Treat a tombstone as absent so deep-linked detail views (which read via get()) show a
    // clean "not found" instead of rendering an empty-payload deleted record. The all()/
    // childrenOf() selectors already exclude deleted; get() was the one leak.
    return e && !e.deleted ? e : undefined;
  }

  private all<P>(type: EntityType): StoredEntity<P>[] {
    const out: StoredEntity<P>[] = [];
    for (const e of this.byKey.values()) {
      if (e.type === type && !e.deleted) out.push(e as StoredEntity<P>);
    }
    return out;
  }

  private childrenOf<P>(type: EntityType, parentId: string): StoredEntity<P>[] {
    return this.all<P>(type).filter((e) => e.parentId === parentId);
  }

  // --- typed selectors ---
  projects(): StoredEntity<ProjectPayload>[] {
    return this.all<ProjectPayload>("PROJECT").sort(byCreatedAtDesc);
  }
  rooms(projectId: string): StoredEntity<RoomPayload>[] {
    return this.childrenOf<RoomPayload>("ROOM", projectId).sort(byCreatedAtDesc);
  }
  photos(roomId: string): StoredEntity<PhotoPayload>[] {
    return this.childrenOf<PhotoPayload>("PHOTO", roomId).sort(byCreatedAtAsc);
  }
  sessions(roomId: string): StoredEntity<SessionPayload>[] {
    return this.childrenOf<SessionPayload>("SESSION", roomId).sort(byCreatedAtDesc);
  }
  cycles(sessionId: string): StoredEntity<CyclePayload>[] {
    return this.childrenOf<CyclePayload>("CYCLE", sessionId).sort(
      (a, b) => (a.payload.index ?? 0) - (b.payload.index ?? 0),
    );
  }
  ideas(roomId: string): StoredEntity<IdeaPayload>[] {
    return this.childrenOf<IdeaPayload>("IDEA", roomId).sort(byCreatedAtDesc);
  }
  scans(roomId: string): StoredEntity<ScanPayload>[] {
    return this.childrenOf<ScanPayload>("SCAN", roomId).sort(byCreatedAtAsc);
  }

  // --- sharing selectors ---

  /** The caller's editor/viewer membership for a shared project (undefined ⇒ own project). */
  membership(projectId: string): Membership | undefined {
    return this.membershipsById.get(projectId);
  }

  /** All shared-project memberships (editor/viewer). */
  memberships(): Membership[] {
    return [...this.membershipsById.values()];
  }

  /** The caller's role for a project. No membership row ⇒ it's their own ⇒ owner. */
  roleFor(projectId: string | null | undefined): ProjectRole {
    if (!projectId) return "owner";
    return this.membershipsById.get(projectId)?.role ?? "owner";
  }

  /** Root PROJECT id of any entity via the parent-chain walk (null if unresolvable). */
  projectIdOf(type: EntityType, id: string): string | null {
    let cur = this.byKey.get(key(type, id));
    for (let hops = 0; cur && hops < 6; hops++) {
      if (cur.type === "PROJECT") return cur.id;
      const parentType = PARENT_TYPE[cur.type];
      if (!parentType || !cur.parentId) return null;
      cur = this.byKey.get(key(parentType, cur.parentId));
    }
    return null;
  }

  /** All saved-idea source filenames in a room (for filled-heart dedupe state). */
  ideaSourceFileNames(roomId: string): Set<string> {
    const s = new Set<string>();
    for (const idea of this.ideas(roomId)) {
      const src = idea.payload.sourceFileName;
      if (src) s.add(src);
    }
    return s;
  }

  assetForLocalPath(localPath: string): AssetRef | undefined {
    return this.assetByLocalPath.get(localPath);
  }

  /** s3Key for a render/photo referenced by its local "<uuid>.<ext>" filename. */
  s3KeyForPhotoFile(fileName: string): string | undefined {
    return this.assetByLocalPath.get(`photos/${fileName}`)?.s3Key;
  }
}

// Memberships with transient-error retry. Returns null (⇒ own-only store) on 404
// (pre-sharing backend) OR any non-auth failure that survives the retries — a 5xx
// (throttling / partial deploy) or network error must NOT fail the whole bootstrap
// when GET /v1/sync is healthy. Mirrors iOS refreshMemberships, which swallows all
// errors and continues own-scope-only. Only 401 propagates for auth handling.
async function membershipsWithRetry(attempts = 3): Promise<Membership[] | null> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fetchMemberships();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      if (e instanceof ApiError && e.status === 401) throw e;
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  return null;
}

// Retry a sync page on transient/network errors (flaky networks); a real 401
// propagates immediately so auth can handle it, and a 403 propagates so the
// scope loop can drop a revoked membership instead of retrying it.
async function pullWithRetry(since: string, scope?: string, attempts = 4): Promise<SyncPullPage> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await syncPull(since, 300, scope);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw e;
      lastErr = e;
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw lastErr;
}

const createdAt = (e: StoredEntity<unknown>): number => {
  const c = (e.payload as { createdAt?: string }).createdAt;
  return c ? Date.parse(c) || 0 : 0;
};
const byCreatedAtDesc = <T>(a: StoredEntity<T>, b: StoredEntity<T>) => createdAt(b) - createdAt(a);
const byCreatedAtAsc = <T>(a: StoredEntity<T>, b: StoredEntity<T>) => createdAt(a) - createdAt(b);
